import { Image } from 'expo-image';
import React from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { VerticalPitchMarkings } from '@/components/ui/VerticalPitchMarkings';
import { useThemePalette } from '@/config/theme';
import { usePublicSquad } from '@/features/fantasy/hooks';
import type { PublicSquadPick } from '@/lib/data/schemas';
import { formatNumber, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import {
  PITCH_VERTICAL_PADDING,
  PLAYER_TOKEN_WIDTH,
  ROW_GAP,
  ROW_MIN_HEIGHT,
} from '@/lib/pitch/formation';
import type { LeaderboardEntry } from '@/lib/supabase/types';
import type { Position } from '@scoring/rules.ts';

/** Pitch rows top→bottom: attackers up front, keeper at the back. */
const ROW_ORDER: Position[] = ['FWD', 'MID', 'DEF', 'GK'];

function Flag({ uri, size = 22 }: { uri?: string | null; size?: number }) {
  const palette = useThemePalette();
  if (!uri) {
    return (
      <View
        style={{ width: size, height: size, borderRadius: 4, backgroundColor: palette.surface2 }}
      />
    );
  }
  return (
    <Image source={{ uri }} style={{ width: size, height: size, borderRadius: 4 }} contentFit="cover" cachePolicy="memory-disk" />
  );
}

function PointsPill({ value }: { value: number | null | undefined }) {
  const { arabicNumerals } = useLocale();
  if (value == null) return null;
  // Negatives keep their own minus sign - never render "+-3".
  const sign = value > 0 ? '+' : '';
  return (
    <AppText variant="micro" weight="bold" className="text-volt-bright">
      {sign}
      {localizeDigits(String(value), arabicNumerals)}
    </AppText>
  );
}

function ArmBand({ pick }: { pick: PublicSquadPick }) {
  const palette = useThemePalette();
  if (!pick.is_captain && !pick.is_vice) return null;
  return (
    <View
      className={`absolute -end-1 -top-1 h-4 w-4 items-center justify-center rounded-full ${
        pick.is_captain ? 'bg-volt' : 'bg-surface-2 border border-volt-line'
      }`}
    >
      <AppText
        variant="micro"
        weight="bold"
        style={{ fontSize: 9, color: pick.is_captain ? palette.onVolt : palette.voltBright }}
      >
        {pick.is_captain ? 'C' : 'V'}
      </AppText>
    </View>
  );
}

function PitchToken({ pick }: { pick: PublicSquadPick }) {
  return (
    <View style={{ width: PLAYER_TOKEN_WIDTH }} className="items-center">
      <View>
        <Flag uri={pick.flag_url} size={26} />
        <ArmBand pick={pick} />
      </View>
      <AppText variant="micro" weight="bold" numberOfLines={1} className="mt-1 text-center text-ink">
        {pick.known_as ?? pick.name}
      </AppText>
      <PointsPill value={pick.points} />
    </View>
  );
}

export interface SquadViewModalProps {
  /** The leaderboard row that was tapped; null closes the modal. */
  entry: LeaderboardEntry | null;
  onClose: () => void;
}

/**
 * Read-only window into a rival manager's squad, opened from a leaderboard row.
 * Backed by the get_public_squad RPC - the server only reveals lineups whose
 * deadline has passed (your own squad is always visible).
 */
export function SquadViewModal({ entry, onClose }: SquadViewModalProps) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const insets = useSafeAreaInsets();
  const query = usePublicSquad(entry?.fantasy_team_id);
  const data = query.data;

  const picks = data?.picks ?? [];
  const starters = ROW_ORDER.reduce<Record<Position, PublicSquadPick[]>>(
    (rows, position) => {
      rows[position] = picks
        .filter((pick) => pick.is_starting && pick.position === position)
        .sort((a, b) => a.slot - b.slot);
      return rows;
    },
    { GK: [], DEF: [], MID: [], FWD: [] },
  );
  const bench = picks.filter((pick) => !pick.is_starting).sort((a, b) => a.slot - b.slot);

  const headerName = data?.team.name ?? entry?.team_name ?? '';
  const headerManager = data?.team.manager ?? entry?.display_name ?? '';
  const headerPoints = data?.team.total_points ?? entry?.total_points ?? 0;

  return (
    <Modal
      visible={entry !== null}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <Pressable className="flex-1 justify-end bg-black/60" onPress={onClose}>
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="max-h-[88%] rounded-t-3xl border border-line bg-surface-2"
          style={{ paddingBottom: insets.bottom + 12 }}
        >
          {/* Header */}
          <View className="flex-row items-center gap-3 border-b border-line px-5 pb-3 pt-5">
            <PlayerAvatar uri={data?.team.avatar_url ?? null} name={headerManager || headerName} size={44} />
            <View className="flex-1">
              <AppText variant="heading" numberOfLines={1} className="text-ink">
                {headerName}
              </AppText>
              <AppText variant="micro" numberOfLines={1} className="mt-0.5 text-muted">
                {t('fantasy.leaderboards.managedBy', { manager: headerManager })}
                {entry ? `  ·  #${formatNumber(entry.rank, numeralOptions)}` : ''}
              </AppText>
              {data?.team.favorite_team_name ? (
                <View className="mt-1 flex-row items-center gap-1.5">
                  <TeamCrest
                    uri={data.team.favorite_team_logo ?? null}
                    name={data.team.favorite_team_name}
                    size="sm"
                  />
                  <AppText variant="micro" weight="medium" className="text-soft" numberOfLines={1}>
                    {data.team.favorite_team_name}
                  </AppText>
                </View>
              ) : null}
            </View>
            <View className="items-end">
              <AppText variant="title" weight="bold" tabular className="text-volt">
                {formatNumber(headerPoints, numeralOptions)}
              </AppText>
              <AppText variant="micro" className="uppercase tracking-wide text-muted">
                {t('fantasy.myTeam.totalPoints')}
              </AppText>
            </View>
          </View>

          {query.isPending ? (
            <View className="gap-3 p-5">
              <Skeleton className="h-64 w-full" />
            </View>
          ) : query.isError ? (
            <View className="p-4">
              <ErrorState error={query.error} onRetry={() => query.refetch()} />
            </View>
          ) : data?.picks == null || data.picks.length === 0 ? (
            <EmptyState
              icon="lock-closed-outline"
              title={t('fantasy.leaderboards.squadHiddenTitle')}
              body={t('fantasy.leaderboards.squadHiddenBody')}
            />
          ) : (
            <ScrollView contentContainerStyle={{ paddingBottom: 16 }}>
              {data.gameweek ? (
                <View className="px-5 pb-1 pt-3">
                  <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
                    {data.gameweek.round_label}
                  </AppText>
                </View>
              ) : null}

              {/* Turf */}
              <View
                style={{ backgroundColor: palette.pitch }}
                className="mx-4 mt-2 overflow-hidden rounded-card border border-pitch-line px-2"
              >
                {/* Shared self-measuring markings — halfway line, centre circle
                    & spot, both penalty boxes, goal areas and penalty spots —
                    drawn behind the tokens, identical to the match-lineup and
                    squad-builder pitches (attack at top, keeper at bottom). */}
                <VerticalPitchMarkings />
                <View style={{ rowGap: ROW_GAP, paddingVertical: PITCH_VERTICAL_PADDING }}>
                  {ROW_ORDER.map((position) => {
                    const row = starters[position];
                    if (row.length === 0) return null;
                    return (
                      <View
                        key={position}
                        className="flex-row justify-evenly"
                        style={{ minHeight: ROW_MIN_HEIGHT }}
                      >
                        {row.map((pick) => (
                          <PitchToken key={pick.player_id} pick={pick} />
                        ))}
                      </View>
                    );
                  })}
                </View>
              </View>

              {/* Bench */}
              {bench.length > 0 ? (
                <View className="mx-4 mt-3 rounded-card border border-line bg-surface px-3 py-3">
                  <AppText variant="micro" weight="bold" className="mb-2 uppercase text-muted">
                    {t('fantasy.leaderboards.bench')}
                  </AppText>
                  <View className="flex-row flex-wrap gap-x-1 gap-y-3">
                    {bench.map((pick) => (
                      <PitchToken key={pick.player_id} pick={pick} />
                    ))}
                  </View>
                </View>
              ) : null}
            </ScrollView>
          )}

          <View className="px-5 pt-3">
            <Button label={t('common.close')} variant="secondary" compact onPress={onClose} />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
